import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MemberRole, Permission, PermissionOverrides } from '@mustauto/types/permissions';
import { api } from '@/lib/api';

/**
 * A equipe da oficina ativa, com o papel DESTA oficina (não o perfil da conta),
 * os ajustes e o que cada um pode fazer de fato — `GET /workshops/my/members`.
 * Plano: docs/ref/mustauto_permissoes_plano.md (Fase 2).
 */
export interface TeamMember {
  userId: string;
  name: string;
  /** Só vem para quem pode mudar a equipe. */
  phone: string | null;
  avatarUrl: string | null;
  /** Texto livre no banco: participação de papel legado vale como Mecânico. */
  role: string;
  isOwner: boolean;
  /** Só o que difere do padrão do papel. */
  overrides: PermissionOverrides;
  permissions: Permission[];
  joinedAt: string;
  appVersion: string | null;
  /** Algum aparelho usado nas últimas duas semanas roda app que não entende as permissões. */
  outdatedApp: boolean;
  /** Trabalha nos carros nesta oficina: Mecânico sempre, os outros pela marca da ficha. */
  worksOnCars: boolean;
  /** Por que quem lê não pode mexer nesta pessoa; `null` = pode. */
  lockedReason: 'owner' | 'self' | 'no_permission' | 'outranks' | null;
}

export interface TeamViewer {
  userId: string;
  isOwner: boolean;
  permissions: Permission[];
}

export const TEAM_MEMBERS_KEY = ['workshop-members'] as const;

export function useTeamMembers() {
  return useQuery({
    queryKey: TEAM_MEMBERS_KEY,
    queryFn: async () => {
      const res = await api.get('/workshops/my/members');
      return res.data as { members: TeamMember[]; viewer: TeamViewer };
    },
  });
}

/**
 * Devolve a promessa da releitura da equipe: a mutação só termina quando a
 * lista nova chegou. Sem isso, o segundo interruptor tocado na janela entre
 * salvar e reler montava o ajuste em cima da lista velha e desfazia o primeiro.
 */
function useInvalidateTeam() {
  const queryClient = useQueryClient();
  return async () => {
    void queryClient.invalidateQueries({ queryKey: ['team-history'] });
    // Desempenho e Escalas leem a mesma equipe.
    void queryClient.invalidateQueries({ queryKey: ['team-stats'] });
    await queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY });
  };
}

/**
 * Muda o papel e/ou os ajustes. Mudar o papel sem mandar ajustes volta ao
 * padrão do papel novo (decisão 6 da prévia) — a tela avisa antes.
 */
export function useUpdateMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async ({ userId, ...change }: { userId: string; role?: MemberRole; overrides?: PermissionOverrides }) => {
      const res = await api.patch(`/workshops/my/members/${userId}`, change);
      return res.data as { role: string; overrides: PermissionOverrides; permissions: Permission[] };
    },
    onSettled: invalidate,
  });
}

/** "Também trabalha nos carros" (perfil único, 5a): a própria pessoa sempre pode; nos outros, quem muda a equipe. */
export function useSetWorksOnCars() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async ({ userId, worksOnCars }: { userId: string; worksOnCars: boolean }) => {
      const res = await api.put(`/workshops/my/members/${userId}/works-on-cars`, { worksOnCars });
      return res.data as { worksOnCars: boolean };
    },
    onSettled: invalidate,
  });
}

export function useRemoveMember() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/workshops/my/members/${userId}`);
    },
    onSuccess: invalidate,
  });
}

export function useTransferOwnership() {
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.post('/workshops/my/owner/transfer', { userId });
    },
    onSuccess: invalidate,
  });
}

export interface TeamEvent {
  id: string;
  action: string;
  actor: { userId: string; name: string } | null;
  target: { userId: string; name: string } | null;
  details: Record<string, unknown>;
  createdAt: string;
}

/** O histórico da equipe, do mais novo para o mais antigo, em páginas. */
export function useTeamHistory(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['team-history'],
    enabled,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '30' });
      if (pageParam) params.set('before', pageParam);
      const res = await api.get(`/workshops/my/team-history?${params.toString()}`);
      return res.data as { events: TeamEvent[]; nextBefore: string | null };
    },
    getNextPageParam: (last) => last.nextBefore,
  });
}
